import * as React from 'react'
import styled from 'styled-components'
import { inject, observer } from 'mobx-react'
import { LightBlueBtn, RedBtn } from 'src/components/atoms/input/buttons'
import { ConfiguratorText } from 'src/components/atoms/configurator'
import { ConfiguratorBox } from 'src/components/containers/configurator/ConfiguratorBox'
import { StoreProps } from 'src/stores'
import FormContainer from 'src/components/containers/FormContainer'
import { TInput } from 'src/components/atoms/input/TextInput'
import { media } from 'src/utils.js'
import CustomModal from 'src/components/containers/CustomModal'

interface GuestListProps extends StoreProps {
  index?: number
}

@inject('confStore')
@observer
export default class GuestList extends React.Component<GuestListProps, any> {
  constructor(props) {
    super(props)
    const { confStore } = this.props
    this.state = {
      openModal: false,
      modalInitialValues: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        comment: ''
      },
      guests: confStore.getDocumentsEntriesByType('guest')
    }
  }

  openModal = (firstName?, lastName?, phone?, email?, comment?, index?) => {
    const { modalInitialValues } = this.state
    const updatedValues = modalInitialValues
    updatedValues.firstName = firstName
    updatedValues.lastName = lastName
    updatedValues.phone = phone
    updatedValues.email = email
    updatedValues.comment = comment
    updatedValues.index = index
    this.setState({ openModal: true, modalInitialValues: updatedValues })
  }

  closeModal = (firstName, lastName, phone, email, comment, index) => {
    if (firstName && !(index >= 0)) {
      // Adding a new guest
      const { confStore } = this.props
      const { guests } = this.state
      const newGuests = guests
      const data = new Map()
      data.set('firstName', firstName)
      data.set('lastName', lastName)
      if (phone !== null) data.set('phone', phone)
      if (email !== null) data.set('email', email)
      if (comment !== null) data.set('comment', comment)
      newGuests.push(data)
      confStore.setDocumentsEntry('guest', newGuests)
      this.setState({ guests: newGuests })
    } else if (firstName && index >= 0) {
      // Updating the guest
      const { confStore } = this.props
      const { guests } = this.state
      const updated = guests
      updated[index].set('firstName', firstName)
      updated[index].set('lastName', lastName)
      updated[index].set('phone', phone)
      updated[index].set('email', email)
      updated[index].set('comment', comment)
      confStore.setDocumentsEntry('guest', updated)
      this.setState({ guests: updated })
    }
    this.setState({ openModal: false })
  }

  deleteGuest = (index) => {
    const { confStore } = this.props
    const { guests } = this.state
    const updated = guests
    updated.splice(index, 1)
    confStore.setDocumentsEntry('guest', updated)
    this.setState({ guests: updated })
  }

  render() {
    const { index } = this.props
    const { openModal, modalInitialValues, guests } = this.state

    return (
      <>
        <AddGuestModal
          open={openModal}
          onClose={this.closeModal}
          initValues={modalInitialValues}
        />
        <ConfiguratorBox
          title={`${index}. Gästeliste`}
          idLabel={`expand-arrow-guest-list`} 
          collapsable
          defaultCollapsed>
          <ConfiguratorText style={{ marginBottom: 15 }}>
            Welche Leute sollen über Ihren Tod informiert werden und ggf. auf
            Ihre Trauerfeier eingeladen werden?
          </ConfiguratorText>
          {guests.map((e, i) => {
            return (
              <Guest key={i}>
                <p>
                  {e.get('firstName') || ''} {e.get('lastName') || ''}
                </p>
                <p>{e.get('email') || ''}</p>
                <p>{e.get('phone') || ''}</p>
                <Edit
                  onClick={() =>
                    this.openModal(
                      e.get('firstName'),
                      e.get('lastName'),
                      e.get('phone'),
                      e.get('email'),
                      e.get('comment'),
                      i
                    )
                  }>
                  <i className='fas fa-pencil-alt' />
                </Edit>
                <Delete onClick={() => this.deleteGuest(i)}>
                  <i className='far fa-trash-alt' />
                </Delete>
              </Guest>
            )
          })}
          <LightBlueBtn id={`open-guest-list`}  onClick={() => this.openModal('')}>
            Person hinzufügen
          </LightBlueBtn>
        </ConfiguratorBox>
      </>
    )
  }
}

interface Props {
  open: boolean
  onClose: (first, last, phone, email, comment, index) => void
  initValues: {
    firstName: string
    lastName: string
    phone: string
    email: string
    comment: string
    index: BigInteger
  }
}

const AddGuestModal = (props: Props) => {
  const { open, onClose, initValues } = props

  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [comment, setComment] = React.useState('')

  React.useEffect(() => {
    setFirstName(initValues.firstName || '')
    setLastName(initValues.lastName || '')
    setPhone(initValues.phone || '')
    setEmail(initValues.email || '')
    setComment(initValues.comment || '')
  }, [props])

  const valid = () => {
    return firstName !== '' && lastName !== ''
  }

  const close = (save) => {
    if (save && valid) {
      onClose(firstName, lastName, phone, email, comment, initValues.index)
    } else {
      onClose(null, null, null, null, null, null)
    }
    setFirstName('')
    setLastName('')
    setPhone('')
    setEmail('')
    setComment('')
  }

  return (
    <CustomModal
      open={open}
      title='Person hinzufügen'
      cancelX
      closeButtonText='Abbrechen'
      onClose={() => close(false)}>
      <FormContainer>
        <TInput
          name='firstName'
          placeholder='Vorname'
          type='text'
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <TInput
          name='lastName'
          placeholder='Nachname'
          type='text'
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <TInput
          name='email'
          placeholder='E-Mail'
          type='text'
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TInput
          name='phone'
          placeholder='Telefon'
          type='text'
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <TInput
          name='comment'
          placeholder='Weiteres ...'
          type='text'
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </FormContainer>
      <RedBtn disabled={!valid()} id="save-info-modal" onClick={() => close(true)}>
        {initValues.firstName !== '' ? 'Speichern' : 'Hinzufügen'}
      </RedBtn>
    </CustomModal>
  )
}

const Guest = styled.div`
  position: relative;
  padding: 7.5px;
  &:nth-child(even) {
    background: #d5e4e6;
  }

  p {
    display: inline-block;
    width: 27.5%;
    margin: 0;
    padding-right: 3%;
    line-height: 1.6;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${media.tablet`
    p {
      width: 40%;
      &:nth-child(3) {
        display: none;
      }
    }
  `}
  ${media.largePhone`
    p {
      width: 70%;
    }
    p:nth-child(2) {
      display: none;
    }
  `}
`
const Button = styled.div`
  position: absolute;
  width: 25px;
  height: 15px;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  text-align: center;
  line-height: 15px;
  & > i {
    color: #677d7f;
  }
`

const Edit = styled(Button)`
  right: 30px;
`

const Delete = styled(Button)`
  right: 7.5px;
`



// WEBPACK FOOTER //
// ./src/components/organisms/configurator/GuestList.tsx